import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Typography,
  IconButton,
  CircularProgress,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import apiService from '../services/api';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: 8,
    maxWidth: 500,
  },
}));

const DialogHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  borderBottom: '1px solid #e0e0e0',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 4,
  },
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 4,
  },
}));

const ProjectModal = ({ open, onClose, onProjectCreated, projectToEdit = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    address_detail: '',
    status: 1,
    methodology_inspection: '',
    name_providers: '',
    facade_inspector: '',
    notice_reference_no: '',
    highlight: '',
    created_by: 1, // Default user ID, should be dynamic in real app
    building_count: 1,
    project_type: 'Drone & Tapping'
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (projectToEdit) {
      // Map status string to integer if necessary
      let statusValue = 1;
      if (typeof projectToEdit.status === 'string') {
        const statusMap = { 'Active': 1, 'Inactive': 0, 'Completed': 2, 'On Hold': 3 };
        statusValue = statusMap[projectToEdit.status] !== undefined ? statusMap[projectToEdit.status] : 1;
      } else {
        statusValue = projectToEdit.status;
      }

      setFormData({
        name: projectToEdit.name || '',
        description: projectToEdit.description || '',
        address_detail: projectToEdit.address_detail || '',
        status: statusValue,
        methodology_inspection: projectToEdit.methodology_inspection || '',
        name_providers: projectToEdit.name_providers || '',
        facade_inspector: projectToEdit.facade_inspector || '',
        notice_reference_no: projectToEdit.notice_reference_no || '',
        highlight: projectToEdit.highlight || '',
        created_by: projectToEdit.created_by || 1,
        building_count: projectToEdit.building_count || 1,
        project_type: projectToEdit.project_type || 'Drone & Tapping'
      });
    } else {
      setFormData({
        name: '',
        description: '',
        address_detail: '',
        status: 1,
        methodology_inspection: '',
        name_providers: '',
        facade_inspector: '',
        notice_reference_no: '',
        highlight: '',
        created_by: 1,
        building_count: 1,
        project_type: 'Drone & Tapping'
      });
    }
  }, [projectToEdit, open]);

  const handleInputChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Project name is required';
    }
    
    if (!formData.address_detail.trim()) {
      newErrors.address_detail = 'Address detail is required';
    }
    
    if (!formData.facade_inspector.trim()) {
      newErrors.facade_inspector = 'Professional engineering is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      let result;
      const payload = {
        ...formData,
        notice_reference_no: String(formData.notice_reference_no || '').trim(),
      };
      if (projectToEdit) {
        result = await apiService.updateProject(projectToEdit.id, payload);
        console.log('Project updated successfully:', result);
      } else {
        result = await apiService.createProject(payload);
        console.log('Project created successfully:', result);
      }
      
      // Reset form
      if (!projectToEdit) {
        setFormData({
          name: '',
          description: '',
          address_detail: '',
          status: 1,
          methodology_inspection: '',
          name_providers: '',
          facade_inspector: '',
          notice_reference_no: '',
          highlight: '',
          created_by: 1,
          building_count: 1,
          project_type: 'Drone & Tapping'
        });
      }
      
      // Notify parent component
      if (onProjectCreated) {
        onProjectCreated(result, !!projectToEdit);
      }
      
      onClose();
    } catch (error) {
      console.error('Error saving project:', error);
      setErrors({ submit: error.message || 'Failed to save project' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      if (!projectToEdit) {
        setFormData({
          name: '',
          description: '',
          address_detail: '',
          status: 1,
          methodology_inspection: '',
          name_providers: '',
          facade_inspector: '',
          notice_reference_no: '',
          highlight: '',
          created_by: 1,
          building_count: 1,
          project_type: 'Drone & Tapping'
        });
      }
      setErrors({});
      onClose();
    }
  };

  return (
    <StyledDialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogHeader>
        <Typography variant="h6" fontWeight="600">
          {projectToEdit ? 'Edit Project' : 'Create New Project'}
        </Typography>
        <IconButton onClick={handleClose} disabled={Boolean(loading)}>
          <CloseIcon />
        </IconButton>
      </DialogHeader>
      
      <DialogContent sx={{ p: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <StyledTextField
              fullWidth
              label="Project Name"
              placeholder="e.g., Downtown Office Building"
              value={formData.name}
              onChange={handleInputChange('name')}
              error={!!errors.name}
              helperText={errors.name}
              required
              disabled={Boolean(loading)}
              size="small"
            />
          </Grid>
          
          <Grid item xs={12}>
            <StyledTextField
              fullWidth
              label="Location"
              placeholder="e.g., 123 Main St, City"
              value={formData.address_detail}
              onChange={handleInputChange('address_detail')}
              error={!!errors.address_detail}
              helperText={errors.address_detail}
              required
              disabled={loading}
              size="small"
            />
          </Grid>
          
          <Grid item xs={12}>
            <StyledTextField
              fullWidth
              label="Description"
              placeholder="Brief description of the project"
              value={formData.description}
              onChange={handleInputChange('description')}
              multiline
              rows={2}
              disabled={loading}
              size="small"
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <StyledTextField
              fullWidth
              type="number"
              label="Number of Buildings"
              value={formData.building_count}
              onChange={handleInputChange('building_count')}
              inputProps={{ min: 1 }}
              disabled={loading}
              size="small"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <StyledFormControl fullWidth disabled={loading} size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                onChange={handleInputChange('status')}
                label="Status"
              >
                <MenuItem value={1}>Active</MenuItem>
                <MenuItem value={0}>Inactive</MenuItem>
                <MenuItem value={2}>Completed</MenuItem>
                <MenuItem value={3}>On Hold</MenuItem>
              </Select>
            </StyledFormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <StyledFormControl fullWidth disabled={loading} size="small">
              <InputLabel>Project Type</InputLabel>
              <Select
                value={formData.project_type}
                onChange={handleInputChange('project_type')}
                label="Project Type"
              >
                <MenuItem value="Drone & Tapping">Drone & Tapping</MenuItem>
                <MenuItem value="Drone Only">Drone Only</MenuItem>
                <MenuItem value="Tapping Only">Tapping Only</MenuItem>
              </Select>
            </StyledFormControl>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <StyledTextField
              fullWidth
              label="Professional Engineering"
              value={formData.facade_inspector}
              onChange={handleInputChange('facade_inspector')}
              error={!!errors.facade_inspector}
              helperText={errors.facade_inspector}
              required
              disabled={loading}
              size="small"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <StyledTextField
              fullWidth
              label="Notice Reference No"
              value={formData.notice_reference_no}
              onChange={handleInputChange('notice_reference_no')}
              disabled={loading}
              size="small"
            />
          </Grid>
          
          {errors.submit && (
            <Grid item xs={12}>
              <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                {errors.submit}
              </Typography>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      
      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button 
          onClick={handleClose} 
          disabled={loading}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {loading ? 'Saving...' : (projectToEdit ? 'Save Changes' : 'Create Project')}
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default ProjectModal;
